import React, { useEffect, useState } from 'react';
import { useTrack } from 'stash/hooks/useTrack';
import {
	Box,
	Flex,
	PrimaryButton,
	SecondaryButton,
	TertiaryButton,
	ThemePreferenceProvider,
	ThemeProvider,
} from '@stashinvest/ui';
import { Carousel } from './carousel/Carousel';
import { BanjoString, useBanjo } from '@stashinvest/react-banjo';
import styled from 'styled-components';
import { setCookie } from 'stash/utils/cookies';
import {
	v1_Login_Clicked,
	v1_UserSignup_Clicked,
	v1_WelcomeScreen_Viewed,
} from '@stashinvest/product-events-javascript';
import { Screen } from '@stashinvest/product-events-javascript/lib/enum_properties';
import useGetLoginUrl from 'stash/hooks/useLoginUrl';
import useAuthRedirect from 'stash/apps/onboarding/hooks/useAuthRedirect';
import { isAndroid, isFBorIGWebView } from 'stash/utils';

const ButtonStyles = styled(Box)`
	button {
		span {
			font-weight: 500;
			font-size: 18px;
		}
	}
`;

const LoginButton = styled(SecondaryButton)`
	height: 50px;

	span {
		font-weight: 500;
		font-size: 16px;
	}
`;

export const PublicWelcomeCarousel = () => {
	const { getString } = useBanjo();
	const track = useTrack();
	const { getLoginUrl } = useGetLoginUrl();
	const { authRedirect } = useAuthRedirect();

	const [slideInfo, setSlideInfo] = useState({
		slideName: 'welcome',
		slideNumber: 1,
	});

	useEffect(() => {
		track(v1_WelcomeScreen_Viewed());
	}, [track]);

	async function handleLoginClick() {
		if (isAndroid() && isFBorIGWebView()) {
			// Do not set cookie for android FB or IG webview
			// We want the users to always see the welcome carousel if they are on android FB or IG webview
			// This is to handle a specific bug when BustingWebView is not working for Users who have disabled Chrome browser on their android device
		} else {
			setCookie('hasSeenWelcomeCarousel', 'true');
		}

		track(
			v1_Login_Clicked({
				screen: Screen.welcomePage,
			})
		);

		window.location = getLoginUrl();
	}

	async function handleSignupClick() {
		if (isAndroid() && isFBorIGWebView()) {
			// Do not set cookie for android FB or IG webview
			// We want the users to always see the welcome carousel if they are on android FB or IG webview
			// This is to handle a specific bug when BustingWebView is not working for Users who have disabled Chrome browser on their android device
		} else {
			setCookie('hasSeenWelcomeCarousel', 'true');
		}
		track(
			v1_UserSignup_Clicked({
				screen: Screen.welcomePage,
			})
		);

		window.location = authRedirect;
	}

	const carouselSlides = [
		{
			img: '/images/illustrations/scene/welcomeCarousel/stashLogo/stashLogo.svg',
			mobileImg:
				'/images/illustrations/scene/welcomeCarousel/stashLogoMobile/stashLogoMobile.svg',
			title: getString('information/sell/personalFinanceAward'),
			desc: '',
			slideName: 'welcome',
		},
		{
			img: '/images/illustrations/scene/welcomeCarousel/stockChart/stockChart.svg',
			mobileImg:
				'/images/illustrations/scene/welcomeCarousel/stockChartMobile/stockChartMobile.svg',
			title: getString('information/sell/investLongTerm'),
			desc: getString('information/sell/whyInvestLongTerm'),
			slideName: 'invest',
		},
		{
			img: '/images/illustrations/scene/welcomeCarousel/balanceScales/balanceScales.svg',
			mobileImg:
				'/images/illustrations/scene/welcomeCarousel/balanceScalesMobile/balanceScalesMobile.svg',
			title: getString('information/sell/smartDecisions'),
			desc: getString('information/sell/whyRegisteredInvestmentAdvisor'),
			slideName: 'registered_investment_advisor',
		},
		{
			img: '/images/illustrations/scene/welcomeCarousel/schedule/schedule.svg',
			mobileImg:
				'/images/illustrations/scene/welcomeCarousel/scheduleMobile/scheduleMobile.svg',
			title: getString('information/sell/stashWayShort'),
			desc: getString('information/sell/whyAutomatedTools'),
			slideName: 'stash_way',
		},
		{
			img: '/images/illustrations/scene/welcomeCarousel/companies/companies.svg',
			mobileImg:
				'/images/illustrations/scene/welcomeCarousel/companiesMobile/companiesMobile.svg',
			title: getString('information/sell/freeTrialThirtyDays'),
			desc: getString('information/sell/lowMonthlyCost'),
			slideName: 'free_trial',
		},
	];

	return (
		<ThemePreferenceProvider>
			<ThemeProvider>
				<Flex
					width="100vw"
					height="100dvh"
					bg="bgPrimary"
					pt={{ mobile: 'xxs', tablet: 'xl' }}
					position="fixed"
					left="0"
					top="0"
					overflowY={{ mobile: 'auto' }}
					alignItems="center"
				>
					<Box maxWidth="32rem" width="100%" m="auto">
						<Box display={{ mobile: 'block', tablet: 'none' }} px="s" pb="xs">
							<Flex justifyContent="flex-end">
								<Box width="110px">
									<LoginButton onClick={handleLoginClick}>
										<BanjoString stringId={'action/startTask/logIn'} />
									</LoginButton>
								</Box>
							</Flex>
						</Box>
						<Carousel
							slides={carouselSlides}
							sendSlideInfo={setSlideInfo}
							currentSlideInfo={slideInfo}
						/>
						<Box px="s">
							<ButtonStyles>
								<PrimaryButton
									onClick={handleSignupClick}
									mb={{ mobile: 'xxs', tablet: 's' }}
								>
									<BanjoString stringId={'action/startTask/signUp'} />
								</PrimaryButton>
							</ButtonStyles>
							<ButtonStyles display={{ mobile: 'none', tablet: 'block' }}>
								<TertiaryButton onClick={handleLoginClick}>
									<BanjoString stringId={'action/startTask/logIn'} />
								</TertiaryButton>
							</ButtonStyles>
						</Box>
					</Box>
				</Flex>
			</ThemeProvider>
		</ThemePreferenceProvider>
	);
};

export default PublicWelcomeCarousel;
